import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import Header from "./Header.tsx";
import Sidebar from "./Sidebar.tsx";
import MultiMonitor from "./MultiMonitor.tsx";
import styles from "./PumpEff.module.css";
import useTheme from "../hooks/useTheme.ts";
import { sidebarOpenForViewport } from "../context/SidebarProvider.tsx";
import { HiOutlineChartBar } from "react-icons/hi";
import {
  FiTruck,
  FiUser,
  FiMapPin,
  FiDroplet,
  FiActivity,
  FiBox,
  FiRadio,
} from "react-icons/fi";

interface ColumnConfig {
  key: string;
  label: string;
  field: string;
  removable: boolean;
}

interface AvailableChannel {
  value: string;
  label: string;
}

interface ActiveStage {
  id: number;
  fleet: number;
  pad_id: string;
  well_id: string;
  stage_id: number;
  major_stage_id_jdd: string;
  wbs: string;
  start_ts: string;
  elapsed: number;
  last_updated: string;
}

interface FleetGroup {
  fleet: number;
  latestStage: {
    pad_name: string;
    start_ts: number;
  };
}

interface PumpData {
  pump_name: string;
  station: number;
  gear: number;
  throttle: number;
  engine_load: number;
  rpm: number;
  calculated_discharge_rate: number;
  [key: string]: any;
}

interface TreatmentData {
  clean_total_meas: number;
  slurry_total: number;
  slurry_total_calc: number;
  sand_rate_surface: number;
  density_surface: number;
  treating_pressure: number;
  hp: number;
  pump_timestamp?: number;
  [key: string]: any;
}

interface PumpConfig {
  pump_name: string;
  van_id?: string | null;
  van_number?: string | number | null;
  vanNumber?: string | number | null;
  van?: string | number | null;
  engine_type: string;
  trans_type: string;
  dgb_type: string;
  [key: string]: any;
}

interface CustomerWellResult {
  fleet_id: number;
  pad: {
    id: number;
    job_number: number;
    pad_name: string;
    well_count: number;
    stage_count: number;
    start_ts: number;
    end_ts: number | null;
    status: string;
  } | null;
  organization: {
    id: number;
    name: string;
  } | null;
  well: {
    id: number;
    well_name: string;
    api: string;
  } | null;
  stage: {
    id: number;
    stage_number: number;
    start_ts: number;
  } | null;
  reason: string;
}

interface CustomerWellData {
  fleet_count: number;
  results: CustomerWellResult[];
}

const AVAILABLE_CHANNELS: AvailableChannel[] = [
  { value: "engine_hours", label: "Engine Hours" },
  { value: "transmission_hours", label: "Transmission Hours" },
  { value: "pe_hours", label: "PE Hours" },
  { value: "fe_hours", label: "FE Hours" },
  { value: "rpm", label: "RPM" },
  { value: "gear", label: "Gear" },
  { value: "fuel_rate", label: "Fuel Rate" },
  { value: "engine_load", label: "Engine Load" },
  { value: "dual_fuel_ratio", label: "Dual Fuel Ratio" },
  { value: "inst_fuel_disp", label: "Inst Fuel Disp" },
  { value: "fan_speed", label: "Fan Speed" },
  { value: "trans_lockup", label: "Trans Lockup" },
  { value: "discharge_pressure", label: "Discharge Pressure" },
  { value: "engine_temp", label: "Engine Coolant Temperature" },
  { value: "engine_oil_pressure", label: "Engine Oil Pressure" },
  { value: "trans_temp", label: "Trans Temp" },
  { value: "trans_pressure", label: "Trans Pressure" },
  { value: "pe_temp", label: "Powerend Oil Temperature" },
  { value: "pe_pressure", label: "Powerend Oil Pressure" },
  { value: "throttle", label: "Throttle" },
  { value: "voltage", label: "Voltage" },
  { value: "calculated_discharge_rate", label: "Calculated Discharge Rate" },
  { value: "engine_air_filter_diff", label: "Engine Air Filter Diff" },
  { value: "crankcase_pressure", label: "Crankcase Pressure" },
  { value: "engine_fuel_pressure", label: "Engine Fuel Pressure" },
  { value: "grease_active", label: "Grease Active" },
  { value: "fault_spn_1", label: "Fault SPN 1" },
  { value: "fault_fmi_1", label: "Fault FMI 1" },
  { value: "fault_spn_2", label: "Fault SPN 2" },
  { value: "fault_fmi_2", label: "Fault FMI 2" },
  { value: "fault_spn_3", label: "Fault SPN 3" },
  { value: "fault_fmi_3", label: "Fault FMI 3" },
  { value: "fault_spn_4", label: "Fault SPN 4" },
  { value: "fault_fmi_4", label: "Fault FMI 4" },
  { value: "fault_spn_5", label: "Fault SPN 5" },
  { value: "fault_fmi_5", label: "Fault FMI 5" },
  { value: "station", label: "Station" },
  { value: "discharge_vfd_amps", label: "VFD Amps" },
  { value: "discharge_vfd_dc_link_volts", label: "DC Link Volts" },
  { value: "discharge_motor_winding_a1_temp", label: "Winding A1 Temp" },
  { value: "discharge_motor_winding_b1_temp", label: "Winding B1 Temp" },
  { value: "discharge_motor_winding_c1_temp", label: "Winding C1 Temp" },
  { value: "discharge_motor_de_bearing_temp", label: "DE Bearing Temp" },
  { value: "discharge_motor_nde_bearing_temp", label: "NDE Bearing Temp" },
  { value: "vfd_speed_hz", label: "VFD Speed Hz" },
  { value: "vfd_speed_percent", label: "VFD Speed %" },
  { value: "vfd_power", label: "VFD Power" },
];

const DEFAULT_COLUMNS: ColumnConfig[] = [
  {
    key: "pump_name",
    label: "Unit Number",
    field: "pump_name",
    removable: false,
  },
  { key: "station", label: "Station", field: "station", removable: true },
  { key: "gear", label: "Gear", field: "gear", removable: true },
  { key: "throttle", label: "Throttle", field: "throttle", removable: true },
  {
    key: "engine_load",
    label: "Engine Load",
    field: "engine_load",
    removable: true,
  },
  { key: "rpm", label: "Engine RPM", field: "rpm", removable: true },
];

const MAX_COLUMNS = 11;
const OilWellIcon = ({ className }: { className?: string }) => (
  <svg
    className={className}
    viewBox="0 0 64 64"
    fill="none"
    stroke="currentColor"
    strokeWidth="4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M22 58L32 6L42 58" />
    <path d="M17 58H47" />
    <path d="M24 18H40" />
    <path d="M20 36H44" />
    <path d="M26 18L38 36" />
    <path d="M38 18L26 36" />
    <path d="M22 36L42 58" />
    <path d="M42 36L22 58" />
  </svg>
);

const PumpEff: React.FC = () => {
  const [showSidebar, setShowSidebar] = useState(sidebarOpenForViewport);
  const [viewMode, setViewMode] = useState<"single" | "multi">("single");
  const [fleetGroups, setFleetGroups] = useState<FleetGroup[]>([]);
  const [selectedFleet, setSelectedFleet] = useState<number | null>(null);

  // Data states
  const [pumps, setPumps] = useState<PumpData[]>([]);
  const pumpsRef = useRef<PumpData[]>([]);
  const [treatment, setTreatment] = useState<TreatmentData | null>(null);
  const [pumpConfigs, setPumpConfigs] = useState<Map<string, PumpConfig>>(
    new Map(),
  );
  const [customerWell, setCustomerWell] = useState<CustomerWellData | null>(
    null,
  );
  const [vanId, setVanId] = useState<string | null>(null);
  const [pumpTimestamp, setPumpTimestamp] = useState<number | null>(null);

  // Column management state
  const [columns, setColumns] = useState<ColumnConfig[]>(DEFAULT_COLUMNS);
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>({ field: "station", direction: "asc" });
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Tooltip state
  const [hoveredPump, setHoveredPump] = useState<string | null>(null);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const { theme } = useTheme();
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";

  // Refs for intervals
  const pumpDataInterval = useRef<NodeJS.Timeout | null>(null);
  const customerWellInterval = useRef<NodeJS.Timeout | null>(null);
  const pumpConfigInterval = useRef<NodeJS.Timeout | null>(null);

  // Refs for AbortControllers to cancel in-flight requests
  // Pump data is polled frequently, so we prevent overlapping requests instead of aborting them.
  const pumpRequestRunning = useRef(false);
  const customerWellAbortController = useRef<AbortController | null>(null);
  const pumpConfigAbortController = useRef<AbortController | null>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const addButtonRef = useRef<HTMLButtonElement | null>(null);
  const selectedFleetRef = useRef<number | null>(selectedFleet);

  useEffect(() => {
    selectedFleetRef.current = selectedFleet;
  }, [selectedFleet]);

  useEffect(() => {
    pumpsRef.current = pumps;
  }, [pumps]);

  // Column management functions
  const removeColumn = (columnKey: string) => {
    setColumns(columns.filter((col) => col.key !== columnKey));
  };

  const addColumn = (channelValue: string) => {
    const channel = AVAILABLE_CHANNELS.find((ch) => ch.value === channelValue);
    if (channel && columns.length < MAX_COLUMNS) {
      const newColumn: ColumnConfig = {
        key: channel.value,
        label: channel.label,
        field: channel.value,
        removable: true,
      };
      setColumns([...columns, newColumn]);
      setShowColumnDropdown(false);
    }
  };

  const getAvailableChannels = (): AvailableChannel[] => {
    const selectedFields = columns.map((col) => col.field);

    const priorityChannels = [
      "calculated_discharge_rate",
      "discharge_vfd_amps",
      "discharge_vfd_dc_link_volts",
      "discharge_motor_winding_a1_temp",
      "discharge_motor_winding_b1_temp",
      "discharge_motor_winding_c1_temp",
      "discharge_motor_de_bearing_temp",
      "discharge_motor_nde_bearing_temp",
      "vfd_speed_hz",
      "vfd_speed_percent",
      "vfd_power",
    ];

    return AVAILABLE_CHANNELS.filter(
      (channel) => !selectedFields.includes(channel.value),
    ).sort((a, b) => {
      const aIndex = priorityChannels.indexOf(a.value);
      const bIndex = priorityChannels.indexOf(b.value);

      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }

      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;

      return a.label.localeCompare(b.label);
    });
  };

  const handleSort = (field: string) => {
    let direction: "asc" | "desc" = "asc";
    if (
      sortConfig &&
      sortConfig.field === field &&
      sortConfig.direction === "asc"
    ) {
      direction = "desc";
    }
    setSortConfig({ field, direction });
  };

  const getSortedPumps = () => {
    if (!sortConfig) return pumps;

    return [...pumps].sort((a, b) => {
      const aValue = a[sortConfig.field];
      const bValue = b[sortConfig.field];

      if (aValue === null || aValue === undefined) return 1;
      if (bValue === null || bValue === undefined) return -1;

      if (typeof aValue === "number" && typeof bValue === "number") {
        return sortConfig.direction === "asc"
          ? aValue - bValue
          : bValue - aValue;
      }

      const aString = String(aValue);
      const bString = String(bValue);
      return sortConfig.direction === "asc"
        ? aString.localeCompare(bString)
        : bString.localeCompare(aString);
    });
  };

  const toggleDropdown = () => {
    if (!showColumnDropdown && addButtonRef.current) {
      const rect = addButtonRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + 4,
        left: rect.left,
      });
    }
    setShowColumnDropdown(!showColumnDropdown);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        addButtonRef.current &&
        !addButtonRef.current.contains(event.target as Node)
      ) {
        setShowColumnDropdown(false);
      }
    };

    if (showColumnDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showColumnDropdown]);

  const fetchFleets = useCallback(async () => {
    try {
      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");
      const response = await axios.get(`${backendUrl}/active-stages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const activeStages: ActiveStage[] = response.data;

      const fleetMap = new Map();
      activeStages.forEach((stage) => {
        if (stage.fleet) {
          const existing = fleetMap.get(stage.fleet);
          const stageTimestamp = new Date(stage.last_updated).getTime();

          if (!existing || stageTimestamp > existing.latestStage.start_ts) {
            fleetMap.set(stage.fleet, {
              fleet: stage.fleet,
              latestStage: {
                pad_name: stage.pad_id,
                start_ts: stageTimestamp,
              },
            });
          }
        }
      });

      const groups: FleetGroup[] = Array.from(fleetMap.values()).sort(
        (a, b) => a.fleet - b.fleet,
      );
      setFleetGroups(groups);
    } catch (error) {
      console.error("Error fetching fleets:", error);
      toast.error("Failed to fetch fleet data");
    }
  }, [backendUrl]);

  const fetchPumpData = useCallback(async () => {
    if (!selectedFleet) return;

    const fleetAtRequest = selectedFleet;

    // Avoid aborting every 1-second poll. Aborted XHRs make Chrome show
    // "Failed to load response data" even when the next request succeeds.
    if (pumpRequestRunning.current) return;
    pumpRequestRunning.current = true;

    try {
      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");
      const response = await axios.get(
        `${backendUrl}/latest-treatment-pump-data-multi?fleets=${fleetAtRequest}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (selectedFleetRef.current !== fleetAtRequest) return;

      const data =
        response.data?.[fleetAtRequest] ??
        response.data?.[String(fleetAtRequest)];

      if (data?.pumps) {
        setPumps(data.pumps);
        // fetchPumpConfigsFromInterval(data.pumps);
      }

      if (data?.treatment) {
        setTreatment(data.treatment);
      }

      const responseVanId =
        data?.van_id ?? data?.van_number ?? data?.vanNumber ?? data?.van;
      if (
        responseVanId !== null &&
        responseVanId !== undefined &&
        String(responseVanId).trim() !== ""
      ) {
        setVanId(String(responseVanId).trim());
      }

      if (data?.pump_timestamp) {
        setPumpTimestamp(data.pump_timestamp);
      }
    } catch (error: any) {
      console.error("Error fetching pump data:", error);
    } finally {
      pumpRequestRunning.current = false;
    }
  }, [backendUrl, selectedFleet]);

  const fetchCustomerWell = useCallback(async () => {
    if (!selectedFleet) return;

    const fleetAtRequest = selectedFleet;

    if (customerWellAbortController.current) {
      customerWellAbortController.current.abort();
    }

    customerWellAbortController.current = new AbortController();

    try {
      const token =
        localStorage.getItem("jwt") || localStorage.getItem("token");
      const response = await axios.post(
        `${backendUrl}/current_customer_well`,
        {
          fleet_ids: [fleetAtRequest],
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          signal: customerWellAbortController.current.signal,
        },
      );

      if (selectedFleetRef.current !== fleetAtRequest) return;

      setCustomerWell(response.data);
    } catch (error: any) {
      if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
        return;
      }
      console.error("Error fetching customer/well data:", error);
    }
  }, [backendUrl, selectedFleet]);

  const fetchPumpConfigsFromInterval = useCallback(
    async (pumpRows?: PumpData[]) => {
      if (!selectedFleet) return;

      const fleetAtRequest = selectedFleet;

      const sourcePumps = pumpRows || pumpsRef.current;
      if (!sourcePumps.length) return;

      const pumpNames = Array.from(
        new Set(
          sourcePumps
            .map((p: PumpData) => p.pump_name)
            .filter(Boolean)
            .map((name) => String(name).trim().toUpperCase()),
        ),
      );

      if (!pumpNames.length) return;

      try {
        const token =
          localStorage.getItem("jwt") || localStorage.getItem("token");
        if (pumpConfigAbortController.current) {
          pumpConfigAbortController.current.abort();
        }
        pumpConfigAbortController.current = new AbortController();

        const response = await axios.post(
          `${backendUrl}/pump-config/latest`,
          {
            pump_names: pumpNames,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
            signal: pumpConfigAbortController.current.signal,
          },
        );

        if (selectedFleetRef.current !== fleetAtRequest) return;

        const configs = new Map<string, PumpConfig>();

        response.data?.configs?.forEach((config: PumpConfig) => {
          const configPumpName = String(config.pump_name || "")
            .trim()
            .toUpperCase();
          if (configPumpName) {
            configs.set(configPumpName, config);
          }
        });

        setPumpConfigs(configs);
      } catch (error: any) {
        if (error.name === "CanceledError" || error.code === "ERR_CANCELED") {
          return;
        }
        console.error("Error fetching pump configs:", error);
      }
    },
    [backendUrl, selectedFleet],
  );

  const abortAllRequests = useCallback(() => {
    pumpRequestRunning.current = false;

    if (customerWellAbortController.current) {
      customerWellAbortController.current.abort();
      customerWellAbortController.current = null;
    }
    if (pumpConfigAbortController.current) {
      pumpConfigAbortController.current.abort();
      pumpConfigAbortController.current = null;
    }
  }, []);

  const stopPolling = useCallback(() => {
    if (pumpDataInterval.current) {
      clearInterval(pumpDataInterval.current);
      pumpDataInterval.current = null;
    }
    if (customerWellInterval.current) {
      clearInterval(customerWellInterval.current);
      customerWellInterval.current = null;
    }
    if (pumpConfigInterval.current) {
      clearInterval(pumpConfigInterval.current);
      pumpConfigInterval.current = null;
    }
  }, []);

  const clearAllData = useCallback(() => {
    pumpRequestRunning.current = false;
    setPumps([]);
    setTreatment(null);
    setPumpConfigs(new Map());
    setCustomerWell(null);
    setVanId(null);
    setPumpTimestamp(null);
  }, []);

  const startPolling = useCallback(() => {
    stopPolling();

    fetchPumpData();
    fetchCustomerWell();

    pumpDataInterval.current = setInterval(() => {
      if (!pumpRequestRunning.current) {
        fetchPumpData();
      }
    }, 1000);

    customerWellInterval.current = setInterval(() => {
      fetchCustomerWell();
    }, 60000);
  }, [stopPolling, fetchPumpData, fetchCustomerWell, selectedFleet]);

  useEffect(() => {
    fetchFleets();

    const fleetPollInterval = setInterval(() => {
      fetchFleets();
    }, 60000);

    return () => {
      clearInterval(fleetPollInterval);
      if (pumpDataInterval.current) clearInterval(pumpDataInterval.current);
      if (customerWellInterval.current)
        clearInterval(customerWellInterval.current);
      if (pumpConfigInterval.current) clearInterval(pumpConfigInterval.current);
    };
  }, [fetchFleets]);

  useEffect(() => {
    if (selectedFleet) {
      startPolling();
    } else {
      stopPolling();
      clearAllData();
    }

    return () => {
      stopPolling();
    };
  }, [selectedFleet, startPolling, stopPolling, clearAllData]);

  const getPumpRowColor = (pump: PumpData): string => {
    if (pump.gear === 0 && pump.rpm === 0) {
      return "#dc3545";
    }
    if (pump.gear === 0 && pump.rpm > 25) {
      return "#3b82f6";
    }
    return "#7bc342";
  };

  const getPumpStatus = (pump: PumpData): string => {
    if (pump.gear === 0 && pump.rpm === 0) {
      return "Not Running";
    }
    if (pump.gear === 0 && pump.rpm > 25) {
      return "Running/Not Pumping";
    }
    return "Running/Pumping";
  };

  const getStatus = (): string => {
    if (treatment && treatment.slurry_total > 5) {
      return "In Progress";
    }
    return "Inactive";
  };

  const calculateMetrics = () => {
    const inline = pumps.length;
    const pumping = pumps.filter(
      (p) => getPumpStatus(p) === "Running/Pumping",
    ).length;
    const expected = treatment ? +(treatment.hp / 1575).toFixed(0) || 0 : 0;

    return { inline, pumping, expected };
  };

  const getPumpingBoxClass = (): string => {
    const { pumping, expected } = calculateMetrics();

    if (expected === 0) {
      return styles.metric;
    }

    const difference = Math.abs(pumping - expected);
    const percentageDiff = (difference / expected) * 100;

    // Apply color rules
    if (percentageDiff > 30) {
      return styles.metricDanger;
    } else if (percentageDiff >= 15) {
      return styles.metricWarning;
    }

    return styles.metric;
  };

  const calculateAverages = () => {
    if (pumps.length === 0) return { gear: 0, throttle: 0, engineLoad: 0 };

    const totalGear = pumps.reduce((sum, p) => sum + (p.gear || 0), 0);
    const totalThrottle = pumps.reduce((sum, p) => sum + (p.throttle || 0), 0);
    const totalLoad = pumps.reduce((sum, p) => sum + (p.engine_load || 0), 0);

    return {
      gear: (totalGear / pumps.length).toFixed(1),
      throttle: (totalThrottle / pumps.length).toFixed(1),
      engineLoad: (totalLoad / pumps.length).toFixed(1),
    };
  };

  const isDataStale = (): boolean => {
    if (!pumpTimestamp) return false;

    const currentTimeUTC = Date.now();
    const twentyFourHoursInMs = 1 * 60 * 60 * 1000;
    const dataAge = currentTimeUTC - pumpTimestamp;
    console.log("dataAge: " + dataAge);
    console.log("currentTime: " + currentTimeUTC);
    console.log("pumptimestamp: " + pumpTimestamp);
    return dataAge >= twentyFourHoursInMs;
  };

  const handleMouseEnter = (pumpName: string, event: React.MouseEvent) => {
    setHoveredPump(pumpName);
    setTooltipPosition({ x: event.clientX, y: event.clientY });
  };

  const handleMouseMove = (event: React.MouseEvent) => {
    if (hoveredPump) {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }
  };

  const handleMouseLeave = () => {
    setHoveredPump(null);
  };

  const metrics = calculateMetrics();
  const averages = calculateAverages();

  return (
    <section
      className={`${styles.section} ${theme === "dark" ? "dark-theme" : "light-theme"}`}
    >
      <div className={styles.profile}>
        <Header
          title="Pump Efficiency"
          setShowSidebar={setShowSidebar}
          showSidebar={showSidebar}
        />

        <div className={styles.container}>
          <div
            className={`${styles.sidebarStyle} app-sidebar-rail`}
            style={{
              width: showSidebar ? "224px" : "56px",
              transition: "width 0.3s",
            }}
          >
            <Sidebar />
          </div>

          <div className={styles.mainContentStyle}>
            {/* View Mode Toggle */}
            <div className={styles.viewToggleContainer}>
              <button
                className={`${styles.viewToggleButton} ${viewMode === "single" ? styles.active : ""}`}
                onClick={() => setViewMode("single")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                </svg>
                Single Fleet View
              </button>
              <button
                className={`${styles.viewToggleButton} ${viewMode === "multi" ? styles.active : ""}`}
                onClick={() => setViewMode("multi")}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <line x1="9" y1="3" x2="9" y2="21" />
                  <line x1="15" y1="3" x2="15" y2="21" />
                  <line x1="3" y1="9" x2="21" y2="9" />
                  <line x1="3" y1="15" x2="21" y2="15" />
                </svg>
                Multi Fleet View
              </button>
            </div>

            {viewMode === "single" ? (
              <div className={styles.dataContainer}>
                {/* Left Panel - Treatment Summary */}
                <div className={styles.summaryPanel}>
                  {/* Fleet Selection */}
                  <div className={styles.fleetDropdownContainer}>
                    <select
                      value={selectedFleet || ""}
                      onChange={(e) => {
                        const fleet = parseInt(e.target.value);

                        abortAllRequests();

                        stopPolling();

                        clearAllData();

                        setSelectedFleet(fleet);
                      }}
                      className={styles.fleetSelect}
                    >
                      <option value="">Select Fleet</option>
                      {fleetGroups.map((group) => (
                        <option key={group.fleet} value={group.fleet}>
                          Fleet {group.fleet}
                        </option>
                      ))}
                    </select>
                    {selectedFleet && (
                      <button
                        onClick={() => {
                          const url = `${window.location.origin}/monitor?fleet=${selectedFleet}`;
                          const windowFeatures =
                            "width=500,height=650,menubar=no,toolbar=no,location=no,status=no";
                          window.open(
                            url,
                            `fleet-${selectedFleet}`,
                            windowFeatures,
                          );
                        }}
                        className={styles.extractButton}
                        title="Open in new window"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                          <polyline points="15 3 21 3 21 9" />
                          <line x1="10" y1="14" x2="21" y2="3" />
                        </svg>
                        Extract
                      </button>
                    )}
                  </div>

                  {/* Fleet Details */}
                  <div className={styles.summarySectionHeader}>
                    <span className={styles.summaryHeaderDot}></span>
                    <span>Fleet Details</span>
                  </div>

                  <div className={styles.summaryGroup}>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiTruck className={styles.summaryIcon} />
                        <span className={styles.label}>Van:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet ? vanId || "Loading..." : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiUser className={styles.summaryIcon} />
                        <span className={styles.label}>Customer:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? customerWell?.results?.[0]?.organization?.name ||
                            "—"
                          : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiMapPin className={styles.summaryIcon} />
                        <span className={styles.label}>Pad:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? customerWell?.results?.[0]?.pad?.pad_name || "—"
                          : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <OilWellIcon className={styles.oilWellIcon} />
                        <span className={styles.label}>Well:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? customerWell?.results?.[0]?.well?.well_name || "—"
                          : "—"}
                      </span>
                    </div>
                  </div>

                  {/* Pump Metrics */}
                  <div className={styles.summarySectionHeader}>
                    <span className={styles.summaryHeaderDot}></span>
                    <span>Pump Metrics</span>
                  </div>

                  <div className={styles.summaryGroup}>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiDroplet
                          className={`${styles.summaryIcon} ${styles.cleanRateIcon}`}
                        />
                        <span className={styles.label}>Clean Rate:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? `${treatment?.clean_total_meas?.toFixed(2) || "0.00"} BPM`
                          : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiRadio
                          className={`${styles.summaryIcon} ${styles.slurryRateIcon}`}
                        />
                        <span className={styles.label}>Slurry Rate:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? `${treatment?.slurry_total?.toFixed(2) || "0.00"} BPM`
                          : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiActivity
                          className={`${styles.summaryIcon} ${styles.slurryRateIcon}`}
                        />
                        <span className={styles.label}>Calc. Slurry Rate:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? `${treatment?.slurry_total_calc?.toFixed(2) || "0.00"} BPM`
                          : "—"}
                      </span>
                    </div>
                  </div>

                  {/* Material & Pressure */}
                  <div className={styles.summarySectionHeader}>
                    <span className={styles.summaryHeaderDot}></span>
                    <span>Material & Pressure</span>
                  </div>

                  <div className={styles.summaryGroup}>
                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiBox
                          className={`${styles.summaryIcon} ${styles.densityIcon}`}
                        />
                        <span className={styles.label}>Sand Rate:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? `${treatment?.sand_rate_surface?.toFixed(2) || "0.00"} lbs/min`
                          : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiBox
                          className={`${styles.summaryIcon} ${styles.densityIcon}`}
                        />
                        <span className={styles.label}>Sand Conc:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? `${treatment?.density_surface?.toFixed(2) || "0.00"} PPA`
                          : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiActivity
                          className={`${styles.summaryIcon} ${styles.pressureIcon}`}
                        />
                        <span className={styles.label}>STP:</span>
                      </span>
                      <span className={styles.value}>
                        {selectedFleet
                          ? `${treatment?.treating_pressure?.toFixed(2) || "0.00"} PSI`
                          : "—"}
                      </span>
                    </div>

                    <div className={styles.summaryItem}>
                      <span className={styles.summaryLabelWrap}>
                        <FiActivity
                          className={`${styles.summaryIcon} ${styles.pressureIcon}`}
                        />
                        <span className={styles.label}>Status:</span>
                      </span>
                      {selectedFleet ? (
                        <span
                          className={`${styles.value} ${styles.status} ${getStatus() === "In Progress" ? styles.statusActive : styles.statusInactive}`}
                        >
                          {getStatus() || "—"}
                        </span>
                      ) : (
                        <span className={styles.value}>—</span>
                      )}
                    </div>
                  </div>
                  <div className={styles.summaryGroup}>
                    <h4 className={styles.groupTitle}>Pump Status</h4>
                    <div className={styles.metricsContainer}>
                      <div className={styles.metric}>
                        <span className={styles.metricLabel}>Inline:</span>
                        <span className={styles.metricValue}>
                          {selectedFleet ? metrics.inline : ""}
                        </span>
                      </div>
                      <div className={getPumpingBoxClass()}>
                        <span className={styles.metricLabel}>Pumping:</span>
                        <span className={styles.metricValue}>
                          {selectedFleet ? metrics.pumping : ""}
                        </span>
                      </div>
                      <div className={styles.metric}>
                        <span className={styles.metricLabel}>Expected:</span>
                        <span className={styles.metricValue}>
                          {selectedFleet ? metrics.expected : ""}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className={styles.tablePanel}>
                  {!selectedFleet ? (
                    <div className={styles.emptyStateNew}>
                      <div className={styles.emptyStateInner}>
                        <div className={styles.emptyIcon}>
                          <HiOutlineChartBar className={styles.icon} />
                        </div>

                        <div className={styles.emptyTitle}>
                          No Fleet Selected
                        </div>

                        <div className={styles.emptySubtitle}>
                          Choose a fleet to view pump efficiency metrics
                        </div>
                      </div>
                    </div>
                  ) : isDataStale() ? (
                    <div className={styles.noDataContainer}>
                      <div className={styles.noDataMessage}>
                        <svg
                          className={styles.noDataIcon}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                        >
                          <circle cx="12" cy="12" r="10" strokeWidth="2" />
                          <line
                            x1="12"
                            y1="8"
                            x2="12"
                            y2="12"
                            strokeWidth="2"
                            strokeLinecap="round"
                          />
                          <circle cx="12" cy="16" r="1" fill="currentColor" />
                        </svg>
                        <h3>No data available.</h3>
                        <p>The pump data is more than 1 hour old.</p>
                      </div>
                    </div>
                  ) : (
                    <div className={styles.tableContainer}>
                      <table className={styles.table}>
                        <thead>
                          <tr>
                            {columns.map((column) => (
                              <th
                                key={column.key}
                                className={styles.sortableHeader}
                              >
                                <div className={styles.headerContent}>
                                  <span
                                    onClick={() => handleSort(column.field)}
                                    className={styles.headerText}
                                  >
                                    {column.label}
                                    {sortConfig &&
                                      sortConfig.field === column.field && (
                                        <span className={styles.sortIcon}>
                                          {sortConfig.direction === "asc"
                                            ? " ▲"
                                            : " ▼"}
                                        </span>
                                      )}
                                  </span>
                                  {column.removable && (
                                    <button
                                      onClick={() => removeColumn(column.key)}
                                      className={styles.removeColumnBtn}
                                      title="Remove column"
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              </th>
                            ))}
                            {columns.length < MAX_COLUMNS && (
                              <th className={styles.addColumnHeader}>
                                <div className={styles.headerContent}>
                                  <button
                                    ref={addButtonRef}
                                    onClick={toggleDropdown}
                                    className={styles.addColumnBtn}
                                  >
                                    + Add Column ▼
                                  </button>
                                </div>
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {getSortedPumps().map((pump, index) => (
                            <tr
                              key={index}
                              style={{ backgroundColor: getPumpRowColor(pump) }}
                            >
                              {columns.map((column) => (
                                <td
                                  key={column.key}
                                  className={
                                    column.key === "pump_name"
                                      ? styles.pumpName
                                      : ""
                                  }
                                  onMouseEnter={
                                    column.key === "pump_name"
                                      ? (e) =>
                                          handleMouseEnter(pump.pump_name, e)
                                      : undefined
                                  }
                                  onMouseMove={
                                    column.key === "pump_name"
                                      ? handleMouseMove
                                      : undefined
                                  }
                                  onMouseLeave={
                                    column.key === "pump_name"
                                      ? handleMouseLeave
                                      : undefined
                                  }
                                >
                                  {pump[column.field] !== null &&
                                  pump[column.field] !== undefined
                                    ? typeof pump[column.field] === "number"
                                      ? Number.isInteger(pump[column.field])
                                        ? pump[column.field]
                                        : pump[column.field].toFixed(2)
                                      : pump[column.field]
                                    : "N/A"}
                                </td>
                              ))}
                              {columns.length < MAX_COLUMNS && <td></td>}
                            </tr>
                          ))}
                          {pumps.length > 0 && (
                            <tr className={styles.averageRow}>
                              {columns.map((column) => {
                                if (column.key === "pump_name") {
                                  return <td key={column.key}>Average:</td>;
                                } else if (column.key === "gear") {
                                  return (
                                    <td key={column.key}>{averages.gear}</td>
                                  );
                                } else if (column.key === "throttle") {
                                  return (
                                    <td key={column.key}>
                                      {averages.throttle}
                                    </td>
                                  );
                                } else if (column.key === "engine_load") {
                                  return (
                                    <td key={column.key}>
                                      {averages.engineLoad}
                                    </td>
                                  );
                                } else {
                                  return <td key={column.key}>-</td>;
                                }
                              })}
                              {columns.length < MAX_COLUMNS && <td></td>}
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <MultiMonitor embedded={true} />
            )}
          </div>
        </div>
      </div>

      {showColumnDropdown && (
        <div
          ref={dropdownRef}
          className={styles.columnDropdown}
          style={{
            top: `${dropdownPosition.top}px`,
            left: `${dropdownPosition.left}px`,
          }}
        >
          {getAvailableChannels().map((channel) => (
            <div
              key={channel.value}
              onClick={() => addColumn(channel.value)}
              className={styles.dropdownItem}
            >
              {channel.label}
            </div>
          ))}
        </div>
      )}

      {hoveredPump && pumpConfigs.has(hoveredPump) && (
        <div
          className={styles.tooltip}
          style={{
            left: `${tooltipPosition.x + 15}px`,
            top: `${tooltipPosition.y + 15}px`,
          }}
        >
          <div className={styles.tooltipTitle}>{hoveredPump}</div>
          <div className={styles.tooltipItem}>
            <span>Status:</span>
            <span>
              {getPumpStatus(pumps.find((p) => p.pump_name === hoveredPump)!)}
            </span>
          </div>
          <div className={styles.tooltipItem}>
            <span>Engine Type:</span>
            <span>{pumpConfigs.get(hoveredPump)?.engine_type || "N/A"}</span>
          </div>
          <div className={styles.tooltipItem}>
            <span>Transmission Type:</span>
            <span>{pumpConfigs.get(hoveredPump)?.trans_type || "N/A"}</span>
          </div>
          <div className={styles.tooltipItem}>
            <span>Dual Fuel Type:</span>
            <span>{pumpConfigs.get(hoveredPump)?.dgb_type || "N/A"}</span>
          </div>
        </div>
      )}
    </section>
  );
};

export default PumpEff;
